import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsAPI } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  BellIcon, CheckIcon, TrashIcon,
  UserGroupIcon, BriefcaseIcon, DocumentTextIcon, ClockIcon, AtSymbolIcon, Cog6ToothIcon,
} from '@heroicons/react/24/outline';

const NOTIFICATION_TYPES = {
  lead_assigned: { bg: 'bg-blue-100 dark:bg-blue-900/30', iconBg: 'bg-blue-500', icon: UserGroupIcon },
  deal_update: { bg: 'bg-purple-100 dark:bg-purple-900/30', iconBg: 'bg-purple-500', icon: BriefcaseIcon },
  quote_sent: { bg: 'bg-amber-100 dark:bg-amber-900/30', iconBg: 'bg-amber-500', icon: DocumentTextIcon },
  task_reminder: { bg: 'bg-orange-100 dark:bg-orange-900/30', iconBg: 'bg-orange-500', icon: ClockIcon },
  mention: { bg: 'bg-green-100 dark:bg-green-900/30', iconBg: 'bg-green-500', icon: AtSymbolIcon },
  system: { bg: 'bg-gray-100 dark:bg-gray-800', iconBg: 'bg-gray-500', icon: Cog6ToothIcon },
};

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

function relativeTime(dateStr) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const Notifications = () => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');

  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await notificationsAPI.getAll();
      return response.data.data;
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: notificationsAPI.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: notificationsAPI.markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      toast.success('All marked as read');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: notificationsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      toast.success('Notification deleted');
    },
  });

  const unreadCount = notifications?.filter((n) => !n.isRead).length || 0;

  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];
    if (filter === 'unread') return notifications.filter(n => !n.isRead);
    if (filter === 'read') return notifications.filter(n => n.isRead);
    return notifications;
  }, [notifications, filter]);

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.04 } } };
  const itemVariants = { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0 } };

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="h-7 w-40 bg-gray-200 dark:bg-surface-700 rounded-lg animate-pulse" />
        <div className="card-premium rounded-2xl p-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-start gap-4 py-4">
              <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-surface-700 animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-surface-700 rounded w-3/4 animate-pulse" />
                <div className="h-3 bg-gray-100 dark:bg-surface-700 rounded w-1/2 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-5">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Notifications</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : "You're all caught up!"}
          </p>
        </div>
        {unreadCount > 0 && (
          <button onClick={() => markAllAsReadMutation.mutate()} className="btn btn-primary text-sm">
            <CheckIcon className="w-4 h-4 mr-1.5" />
            Mark All Read
          </button>
        )}
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants} className="flex items-center gap-2">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              filter === f.key
                ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-surface-700'
            }`}
          >
            {f.label}
            {f.key === 'unread' && unreadCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-primary-500 text-white rounded-full">{unreadCount}</span>
            )}
          </button>
        ))}
      </motion.div>

      {/* Notifications List */}
      <motion.div variants={itemVariants} className="card-premium rounded-2xl overflow-hidden">
        {filteredNotifications.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-surface-700">
            <AnimatePresence>
              {filteredNotifications.map((notification) => {
                const typeConfig = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.system;
                const IconComp = typeConfig.icon;

                return (
                  <motion.div
                    key={notification._id}
                    layout
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10, height: 0 }}
                    className={`px-5 py-4 transition-colors hover:bg-gray-50 dark:hover:bg-surface-700/50 ${
                      !notification.isRead ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3.5">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${typeConfig.iconBg}`}>
                        <IconComp className="w-5 h-5 text-white" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h3 className={`text-sm font-medium ${!notification.isRead ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                              {notification.title}
                            </h3>
                            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{notification.message}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <time className="text-xs text-gray-400 dark:text-gray-500">{relativeTime(notification.createdAt)}</time>
                              {notification.link && (
                                <Link to={notification.link} className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline">
                                  View details
                                </Link>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!notification.isRead && (
                              <button
                                onClick={() => markAsReadMutation.mutate(notification._id)}
                                className="p-1.5 rounded-lg text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                                title="Mark as read"
                              >
                                <CheckIcon className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteMutation.mutate(notification._id)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {!notification.isRead && (
                          <div className="absolute left-1 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-500 rounded-full hidden sm:block" />
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : (
          <div className="text-center py-16 px-6">
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-surface-700 flex items-center justify-center mx-auto mb-3">
              <BellIcon className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {filter ? 'No matching notifications' : 'No notifications'}
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {filter ? 'Try a different filter.' : "You're all caught up!"}
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Notifications;





